import { z } from "zod";

/**
 * Longitudinal problem list ("قائمة المشاكل") — the patient's standing
 * clinical context, distinct from per-visit diagnoses. Problems are
 * optionally ICD-10 coded and stay active until explicitly resolved.
 */

export const problemStatusSchema = z.enum(["active", "resolved"]);
export type ProblemStatus = z.infer<typeof problemStatusSchema>;

export const patientProblemApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	icd_code: z.string().nullable(),
	label: z.string(),
	status: problemStatusSchema,
	onset_date: z.string().date().nullable(),
	resolved_at: z.string().datetime().nullable(),
	recorded_by: z.string().uuid().nullable(),
	notes: z.string().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type PatientProblemApi = z.infer<typeof patientProblemApiSchema>;

export const createPatientProblemInputSchema = z
	.object({
		patient_id: z.string().uuid(),
		/** Optional ICD-10 code; when given without a label, the server
		 *  autofills the label from the WHO ICD-10 library. */
		icd_code: z.string().trim().max(10).optional(),
		/** Free-text problems are first-class — label OR icd_code required. */
		label: z.string().trim().max(300).optional(),
		onset_date: z.string().date().optional(),
		notes: z.string().max(1000).optional(),
	})
	.refine((v) => Boolean(v.label?.length) || Boolean(v.icd_code?.length), {
		message: "Either label or icd_code is required.",
	});
export type CreatePatientProblemInput = z.infer<
	typeof createPatientProblemInputSchema
>;

export const updatePatientProblemInputSchema = z.object({
	icd_code: z.string().trim().max(10).nullable().optional(),
	label: z.string().trim().min(1).max(300).optional(),
	onset_date: z.string().date().nullable().optional(),
	notes: z.string().max(1000).nullable().optional(),
});
export type UpdatePatientProblemInput = z.infer<
	typeof updatePatientProblemInputSchema
>;
